import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import {Observable, of, Subject} from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { Cacheable, CacheBuster } from 'ts-cacheable';

import { ActivityBlockingService } from './activity-blocking.service';
import { url } from '../helpers/url';
import { SharedSettings } from '../models/shared-settings';
import { Site } from '../models/site';
import { Shift } from '../models/shift';
import { SiteConfig } from '../models/site-config';

@Injectable({
    providedIn: 'root'
})
export class SitesService {
    private static readonly cacheBuster = new Subject<void>();
    private static readonly configCacheBuster = new Subject<void>();

    constructor(
        private http: HttpClient,
        private settings: SharedSettings,
        private activityService: ActivityBlockingService) {
    }

    GetWarehousingSites(): Observable<Site[]> {
        return this.GetSites().pipe(map(sites => sites.filter(s => s.subinventories.length > 0)));
    }

    @Cacheable({
        maxAge: 5 * 1000 * 60,
        cacheBusterObserver: SitesService.cacheBuster
    })
    GetSites(): Observable<Site[]> {
        return this.http.get<Site[]>(this.settings.apiBaseUrl + 'sites').pipe(
            catchError(_err => of(<Site[]>[])),
            this.activityService.Wait('sites service sites'),
            map(sites => {
                sites.sort((a, b) => a.description.localeCompare(b.description));
                sites.forEach(s => s.displayLabel = `${s.description} - ${s.id}`);

                return sites;
            })
        );
    }

    @Cacheable({
        maxAge: 5 * 1000 * 60,
        cacheBusterObserver: SitesService.configCacheBuster
    })
    GetShiftsForSite(siteId: string): Observable<Shift[]> {
        return this.http.get<Shift[]>(this.settings.apiBaseUrl + url`sites/${siteId}/shifts`)
            .pipe(map(shifts => shifts.sort((l, r) => l.name.localeCompare(r.name))));
    }

    @Cacheable({
        maxAge: 5 * 1000 * 60,
        cacheBusterObserver: SitesService.configCacheBuster
    })
    GetSiteConfigs(siteId: string): Observable<SiteConfig[]> {
        return this.http.get<SiteConfig[]>(this.settings.apiBaseUrl + url`sites/${siteId}/config`)
            .pipe(map(configs => configs.map(c => new SiteConfig(c.siteId, c.configKey, c.configValue))));
    }

    @CacheBuster({
        cacheBusterNotifier: SitesService.configCacheBuster
    })
    SetSiteConfig(siteId: string, key: string, value: string): Observable<void> {
        return this.http.put<void>(
            this.settings.apiBaseUrl + url`sites/${siteId}/config/${key}`, JSON.stringify(value),
            { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
    }

    /***
     * Returns whether the user has ANY sites that have quality control-enabled.
     */
    hasAnyQualityControlSites(): Observable<boolean> {
        return this.GetSites().pipe(
            map(sites => sites.some(site => site.hasQualityControlEnabled))
        );
    }
}
